import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
// Components
import DatePicker from 'components/common/DatePicker';
import FilterDropdown from './FilterDropdown';
import CalendarIcon from 'assets/images/svg/CalendarIcon';
import DownArrow from 'assets/images/svg/DownArrow';
// Helpers
import {
  OPS_LEVELS,
  opsLevelOptions,
  filterOpsByLevel,
  normalizeOpsList,
  getMainOpsId,
  statusFilterOptions,
  prepFilterOptions,
} from 'constants/OrdersFilters';
// Actions
import { setOpsNames } from 'store/orders/OrdersListActions';
// API
import OrdersListService from 'services/OrdersListService';
// Selectors
import { getOpsList } from 'store/orders/OrdersListSelectors';
// Localstorage
import { getFromLocalStorage } from 'helpers/LocalStorage';

const OrdersFilterBar = ({ filters, onFiltersChange }) => {
  const [showSingleDatePicker, setShowSingleDatePicker] = useState(false);
  const dispatch = useDispatch();
  const opsList = useSelector((state) => getOpsList({ state }));
  const mainOpsId = getMainOpsId();
  const mainOpsName = getFromLocalStorage('opsName') || 'Main OPS';

  const { deliveryDate, opsLevel, opsId, floristOrderPrep, floristAssignedOrders } = filters;

  const isOpsPickerEnabled = opsLevel === OPS_LEVELS.SUB || opsLevel === OPS_LEVELS.B2B;
  const opsPickerOptions = isOpsPickerEnabled
    ? filterOpsByLevel(opsList, opsLevel).map((ops) => ({ value: ops.id, text: ops.name }))
    : [
        {
          value: opsLevel === OPS_LEVELS.MAIN ? +mainOpsId : '',
          text: opsLevel === OPS_LEVELS.MAIN ? mainOpsName : 'All OPS',
        },
      ];

  // Sub-OPS and B2B OPS come from the same OperationCenter call - load them as soon as
  // either level is picked so the dropdown has something to show.
  const fetchOpsList = async () => {
    if (!mainOpsId) {
      toast.error('No OPS is linked to this account - please log in again.');
      return;
    }
    try {
      const res = await OrdersListService.getSubAndB2bOps(mainOpsId);
      const opsList = normalizeOpsList(res?.data?.data ?? res?.data);
      dispatch({ type: 'SET_OPS_LIST', payload: opsList });
      // Dropped rows only carry an opsId - keep the names these bring in.
      dispatch(setOpsNames(Object.fromEntries(opsList.map((ops) => [ops.id, ops.name]))));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('sub-and-b2b-ops failed', err);
      toast.error(err?.response?.data?.message ?? 'Could not load the OPS list');
      dispatch({ type: 'SET_OPS_LIST', payload: [] });
    }
  };

  const handleOpsLevel = (level) => {
    if (level === opsLevel) {
      return;
    }
    if (level === OPS_LEVELS.SUB || level === OPS_LEVELS.B2B) {
      fetchOpsList();
    }
    // opsId only travels with a level that can actually scope it down.
    onFiltersChange({
      opsLevel: level,
      opsId: level === OPS_LEVELS.MAIN ? mainOpsId : '',
    });
  };

  return (
    <div className="orders-filter-bar">
      <div className="filters-row">
        <div className="filters-left">
          <div
            className="delivery-date-filter filter-pill"
            onClick={() => setShowSingleDatePicker(true)}
          >
            <CalendarIcon cssClass="filter-pill-icon" />
            <span className="filter-pill-label">Delivery date</span>
            <DatePicker
              id="orders-delivery-date"
              label=""
              value={moment(deliveryDate).toDate().toISOString()}
              showSingleDatePicker={showSingleDatePicker}
              setShowSingleDatePicker={setShowSingleDatePicker}
              handleChange={(date) => onFiltersChange({ deliveryDate: date })}
              customProps={{ maxDate: new Date('2030') }}
            />
            <DownArrow customCss={`filter-pill-caret${showSingleDatePicker ? ' upside-down' : ''}`} />
          </div>

          <FilterDropdown
            id="orders-status-filter"
            label="Status"
            options={statusFilterOptions}
            value={floristAssignedOrders}
            onSelect={(option) => onFiltersChange({ floristAssignedOrders: option.value })}
          />
        </div>

        <div className="filters-right">
          <div className="ops-level-toggle">
            {opsLevelOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`ops-level-btn${opsLevel === option.value ? ' active' : ''}`}
                onClick={() => handleOpsLevel(option.value)}
              >
                {option.text}
              </button>
            ))}
          </div>

          <FilterDropdown
            id="orders-ops-picker"
            options={opsPickerOptions}
            value={isOpsPickerEnabled ? opsId : opsPickerOptions[0].value}
            onSelect={(option) => onFiltersChange({ opsId: option.value })}
            disabled={!isOpsPickerEnabled}
            placeholder={opsLevel === OPS_LEVELS.SUB ? 'Select Sub-OPS' : 'Select B2B OPS'}
          />
        </div>
      </div>

      <div className="prep-filter-row">
        <div className="prep-toggle">
          {prepFilterOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`prep-btn${floristOrderPrep === option.value ? ' active' : ''}`}
              onClick={() => onFiltersChange({ floristOrderPrep: option.value })}
            >
              {option.text}
            </button>
          ))}
        </div>
        <p className="prep-hint">
          <b>Needs Prep</b> — SKU requires floral assembly by a florist. <b>No-Prep</b> — SKU is
          auto-included (add-on cards, pre-packaged goods) and skips the preparation flow entirely.
        </p>
      </div>
    </div>
  );
};

export default OrdersFilterBar;
